import React, { useEffect, useState } from 'react';
import {
  getSkuHistory, listAdjustments, createForecastAdjustment, deleteForecastAdjustment,
  getProjectionByCustomer,
} from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { X, Plus, Trash, Clock, ChartBar, UsersThree } from '@phosphor-icons/react';
import { toast } from 'sonner';


export default function SkuForecastDrawer({ sku, onClose, onAdjustmentChange }) {
  const [history, setHistory] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [customerSplit, setCustomerSplit] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newWeek, setNewWeek] = useState(0);
  const [newDelta, setNewDelta] = useState(0);
  const [newReason, setNewReason] = useState('');

  const load = async () => {
    if (!sku) return;
    setLoading(true);
    try {
      const [h, a, c] = await Promise.all([
        getSkuHistory(sku.sku_id, { days: 60 }),
        listAdjustments({ sku_id: sku.sku_id, limit: 50 }),
        getProjectionByCustomer(sku.sku_id, { horizon_weeks: 8, lookback_days: 90, top_n: 5 }),
      ]);
      setHistory(h.data);
      setAdjustments(a.data.rows || []);
      setCustomerSplit(c.data);
    } catch (e) { toast.error('Failed to load SKU detail'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sku?.sku_id]);

  if (!sku) return null;

  const handleCreate = async () => {
    if (!newReason.trim() || newReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    if (!newDelta || Number.isNaN(Number(newDelta))) {
      toast.error('Delta must be a number');
      return;
    }
    try {
      await createForecastAdjustment({
        sku_id: sku.sku_id,
        week_offset: Number(newWeek),
        delta: Number(newDelta),
        reason: newReason.trim(),
      });
      toast.success('Adjustment saved');
      setNewDelta(0); setNewReason(''); setNewWeek(0);
      await load();
      onAdjustmentChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this adjustment?')) return;
    try {
      await deleteForecastAdjustment(id);
      toast.success('Deleted');
      await load();
      onAdjustmentChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Simple SVG sparkline for the 60-day consumption history
  const points = (history?.history || []).map(p => ({ ...p, qty: Number(p.qty) || 0 }));
  const maxQ = Math.max(...points.map(p => p.qty), 1) || 1;
  const spark = points.length > 0 ? points.map((p, i) => {
    const x = (i / Math.max(points.length - 1, 1)) * 300;
    const y = maxQ > 0 ? 60 - (p.qty / maxQ) * 55 : 30;
    return `${x},${Number.isFinite(y) ? y : 30}`;
  }).join(' ') : '';

  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="sku-drawer-backdrop"
    >
      <div
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-background shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="sku-drawer"
      >
        <div className="sticky top-0 bg-background border-b border-border p-5 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider opacity-60 font-mono">SKU Forecast Detail</div>
            <h2 className="text-2xl font-bold tracking-tight mt-0.5 font-mono">{sku.sku_code}</h2>
            <div className="text-sm opacity-70 mt-0.5">{sku.sku_name}</div>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {loading ? (
          <div className="p-5 text-sm opacity-60">Loading detail…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* 60-day history sparkline */}
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ChartBar className="size-4 opacity-60" />
                <h3 className="font-bold text-[13px]">60-day consumption</h3>
              </div>
              {points.length > 0 ? (
                <svg viewBox="0 0 300 60" className="w-full h-20" data-testid="sku-drawer-sparkline">
                  <polyline points={spark} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" />
                </svg>
              ) : (
                <div className="text-[12px] opacity-60">No history</div>
              )}
              <div className="text-[11px] opacity-60 font-mono mt-2">
                Avg {(points.reduce((s, p) => s + p.qty, 0) / Math.max(points.length, 1)).toFixed(1)} units/day ·
                peak {maxQ} units
              </div>
            </Card>

            {/* Customer split */}
            {customerSplit && customerSplit.customers?.length > 0 && (
              <Card className="p-4" data-testid="sku-drawer-customer-split">
                <div className="flex items-center gap-2 mb-3">
                  <UsersThree className="size-4 opacity-60" />
                  <h3 className="font-bold text-[13px]">Demand by customer</h3>
                  <span className="text-[10px] opacity-50 font-mono ml-auto">
                    last {customerSplit.lookback_days}d · top {customerSplit.customers.length}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {customerSplit.customers.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px]"
                         data-testid={`customer-share-${i}`}>
                      <div className="w-32 truncate font-mono">{c.customer_name}</div>
                      <div className="flex-1 h-4 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${c.share_pct}%` }} />
                      </div>
                      <div className="w-14 text-right font-mono font-bold tabular-nums">
                        {c.share_pct}%
                      </div>
                      <div className="w-12 text-right text-[10px] opacity-60 font-mono">
                        {c.historical_qty}u
                      </div>
                    </div>
                  ))}
                  {customerSplit.other_share_pct > 0 && (
                    <div className="flex items-center gap-2 text-[11px] opacity-60 pt-1 border-t border-border/40 mt-2">
                      <div className="w-32 font-mono">+ {customerSplit.other_long_tail_count} others</div>
                      <div className="flex-1 h-3 bg-muted rounded-sm overflow-hidden">
                        <div className="h-full bg-muted-foreground/30" style={{ width: `${customerSplit.other_share_pct}%` }} />
                      </div>
                      <div className="w-14 text-right font-mono">
                        {customerSplit.other_share_pct}%
                      </div>
                      <div className="w-12" />
                    </div>
                  )}
                </div>
                <div className="text-[11px] opacity-60 font-mono mt-3">
                  {customerSplit.total_historical_qty} total units shipped · projected demand this horizon: {customerSplit.total_weekly_demand.reduce((a,b)=>a+b,0).toFixed(0)} units
                </div>
              </Card>
            )}

            {/* Create adjustment */}
            <Card className="p-4">
              <h3 className="font-bold text-[13px] mb-3 flex items-center gap-2">
                <Plus className="size-4" /> New adjustment
              </h3>
              <div className="grid grid-cols-6 gap-2 items-end">
                <div className="col-span-1">
                  <label className="text-[10px] uppercase opacity-60">Week</label>
                  <input type="number" min="0" max="51" value={newWeek}
                         onChange={(e) => setNewWeek(e.target.value)}
                         className="h-8 w-full px-2 text-[12px] bg-background border border-border rounded font-mono"
                         data-testid="drawer-new-week" />
                </div>
                <div className="col-span-1">
                  <label className="text-[10px] uppercase opacity-60">Delta (+/−)</label>
                  <input type="number" value={newDelta}
                         onChange={(e) => setNewDelta(e.target.value)}
                         className="h-8 w-full px-2 text-[12px] bg-background border border-border rounded font-mono"
                         data-testid="drawer-new-delta" />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase opacity-60">Reason</label>
                  <input type="text" value={newReason}
                         placeholder="e.g. Marketing push for spring launch"
                         onChange={(e) => setNewReason(e.target.value)}
                         className="h-8 w-full px-2 text-[12px] bg-background border border-border rounded"
                         data-testid="drawer-new-reason" />
                </div>
                <div>
                  <Button size="sm" onClick={handleCreate} data-testid="drawer-save-adjustment">Save</Button>
                </div>
              </div>
            </Card>

            {/* Active adjustments */}
            <Card className="p-4">
              <h3 className="font-bold text-[13px] mb-3 flex items-center gap-2">
                <Clock className="size-4" /> Active adjustments ({adjustments.length})
              </h3>
              {adjustments.length === 0 ? (
                <div className="text-[12px] opacity-60">No adjustments yet.</div>
              ) : (
                <table className="w-full text-[12px]" data-testid="drawer-adjustments-table">
                  <thead className="text-[10px] uppercase opacity-60">
                    <tr>
                      <th className="text-left pb-2">Week</th>
                      <th className="text-right pb-2">Delta</th>
                      <th className="text-left pb-2">Reason</th>
                      <th className="text-left pb-2">Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustments.map((a) => (
                      <tr key={a.id} className="border-t border-border/40">
                        <td className="py-2 font-mono">wk {a.week_offset}</td>
                        <td className="py-2 text-right font-mono">
                          <span className={a.delta > 0 ? 'text-emerald-600' : 'text-red-600'}>
                            {a.delta > 0 ? '+' : ''}{a.delta}
                          </span>
                        </td>
                        <td className="py-2 opacity-80">{a.reason}</td>
                        <td className="py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                            a.status === 'approved' ? 'bg-emerald-500/20 text-emerald-700' :
                            a.status === 'rejected' ? 'bg-red-500/20 text-red-700' :
                            'bg-amber-500/20 text-amber-700'
                          }`}>{a.status}</span>
                        </td>
                        <td className="py-2 text-right">
                          <button
                            onClick={() => handleDelete(a.id)}
                            className="opacity-60 hover:opacity-100"
                            title="Delete adjustment"
                          >
                            <Trash className="size-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
